import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        yellow: "bg-primaryApp text-white border border-primaryApp hover:bg-primaryApp/90",
        gray: "bg-veryLightGray text-secondaryText border border-veryLightGray hover:border-input",
        green: "bg-lightLimeGreen text-mossGreen hover:bg-lightLimeGreen/90",
        destructive:
          "bg-lightRed text-redApp hover:bg-lightRed/40",
        outline:
          "border border-veryLightGray bg-white text-secondaryText md:hover:bg-backgroundApp",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 underline",
      },
      size: {
        xs: "py-1 px-2",
        default: "py-2 px-3.5",
        sm: "rounded-md px-3",
        lg: "rounded-md h-10 px-8",
        icon: "w-10",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
